import type { ComponentProps } from 'react';

import { AREA_SQUARE_MAX_LENGHT, BUDGET_MAX_LENGHT } from '@crm/entities/deal';
import { MAX_PHONE_LENGTH } from '@crm/shared/lib';

import { EditableDateField } from './EditableDateField';
import { EditableField } from './EditableField';
import { InformationFieldError } from './InformationFieldError';
import {
    EMAIL_MAX_LENGTH,
    type DateFieldCommitHandler,
    type FieldEditHandler,
    type FieldFocusHandler,
    type FieldInputHandler,
    type InfoFieldName,
    type InfoItem,
    isDateField,
    isUpdateDealDetailsFieldName,
    type UpdateFieldHandler,
    type UpdateFieldKeyDownHandler,
} from '../../model/informationBlockFields';

type InfoRowProps = InfoItem & {
    errorMessage?: string;
    onFieldBlur: UpdateFieldHandler;
    onFieldKeyDown: UpdateFieldKeyDownHandler;
    onFieldFocus: FieldFocusHandler;
    onFieldInput: FieldInputHandler;
    onFieldEdit: FieldEditHandler;
    onDateFieldCommit: DateFieldCommitHandler;
};

type EditableFieldInputProps = Pick<
    ComponentProps<'input'>,
    'inputMode' | 'maxLength' | 'type'
>;

const INFO_ROW_CLASS_NAME = 'grid h-4 grid-cols-[140px_1fr] grid-rows-[minmax(0,1fr)] items-center';
const INPUT_PROPS_BY_FIELD_NAME: Partial<Record<InfoFieldName, EditableFieldInputProps>> = {
    area: {
        maxLength: AREA_SQUARE_MAX_LENGHT,
    },
    'approximate_budget': {
        maxLength: BUDGET_MAX_LENGHT,
    },
    email: {
        type: 'email',
        maxLength: EMAIL_MAX_LENGTH,
    },
    phone: {
        type: 'tel',
        inputMode: 'numeric',
        maxLength: MAX_PHONE_LENGTH,
    },
};

export const InfoRow = ({
    label,
    placeholder,
    rawValue,
    value,
    fieldName,
    errorMessage,
    onFieldBlur,
    onFieldKeyDown,
    onFieldFocus,
    onFieldInput,
    onFieldEdit,
    onDateFieldCommit,
}: InfoRowProps) => {
    const isDate = isDateField(fieldName);
    const isReadonlyField = fieldName === 'deal_details';
    const initialValue = rawValue ?? value;
    const errorId = `${fieldName}-error`;
    const shouldUpdateField = isUpdateDealDetailsFieldName(fieldName);
    const inputProps = INPUT_PROPS_BY_FIELD_NAME[fieldName] ?? {};

    if (isReadonlyField) {
        return (
            <div className={INFO_ROW_CLASS_NAME}>
                <div className="text-xs leading-4 font-normal text-smtr-main-tertiary-100">
                    {label}
                </div>
                <div
                    className="min-w-0 truncate px-2 py-1 text-xs leading-4 font-normal text-smtr-main-black-100"
                    title={String(value ?? '')}
                >
                    {value}
                </div>
            </div>
        );
    }

    if (isDate) {
        return (
            <div className={INFO_ROW_CLASS_NAME}>
                <div className="text-xs leading-4 font-normal text-smtr-main-tertiary-100">
                    {label}
                </div>
                <EditableDateField
                    value={value}
                    placeholder={placeholder}
                    aria-label={label}
                    name={fieldName}
                    error={errorMessage}
                    errorDisplay="tooltip"
                    onInput={(event) => {
                        if (shouldUpdateField) {
                            onFieldInput(fieldName, event);
                        }
                    }}
                    onCancel={() => onFieldEdit(fieldName)}
                    onCommit={(nextValue) => onDateFieldCommit(fieldName, initialValue, nextValue)}
                />
            </div>
        );
    }

    return (
        <div className={INFO_ROW_CLASS_NAME}>
            <div className="text-xs leading-4 font-normal text-smtr-main-tertiary-100">{label}</div>
            <div className="group/info-field relative min-w-0">
                <EditableField
                    value={value}
                    {...inputProps}
                    placeholder={placeholder ?? '-'}
                    aria-label={label}
                    aria-invalid={Boolean(errorMessage)}
                    aria-describedby={errorMessage ? errorId : undefined}
                    name={fieldName}
                    className={errorMessage ? 'pr-7' : undefined}
                    onBlur={(event) => {
                        if (shouldUpdateField) {
                            onFieldBlur(fieldName, initialValue, event);
                        }
                    }}
                    onFocus={(event) => {
                        if (rawValue !== undefined && !errorMessage) {
                            event.currentTarget.dataset.rawValue = String(rawValue ?? '');
                        } else {
                            delete event.currentTarget.dataset.rawValue;
                        }

                        if (shouldUpdateField) {
                            onFieldFocus(fieldName, event);
                        }
                    }}
                    onInput={(event) => {
                        if (shouldUpdateField) {
                            onFieldInput(fieldName, event);
                        }
                    }}
                    onKeyDown={(event) => {
                        if (shouldUpdateField) {
                            onFieldKeyDown(fieldName, initialValue, event);
                        }
                    }}
                />
                {errorMessage && <InformationFieldError id={errorId} errorMessage={errorMessage} />}
            </div>
        </div>
    );
};
